import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { Spin, Skeleton, message, Empty } from "antd";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import { getApi } from "../../../../api/api_instance";
import QuestionUtils from "../ViewForm/components/QuestionUtils";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import TextStyles from "../../../../components/Elements/TextStyles/TextStyles";
import { useParams } from "react-router-dom";
import { Button } from "antd";
import { API_URLS } from "../../../../api/apiUrls";
import { ArrowUpOutlined, SaveOutlined } from "@ant-design/icons";

const StandardFormAction = ({
  formAllotmentId,
  formId: propFormId,
  formType,
  standalone,
  hideBackButton: hideBackButtonProp = false,
}) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  const visibleQuestionCount = useMemo(() => {
    if (!Array.isArray(questions)) return 0;
    return questions.reduce((acc, division) => {
      const qs = Array.isArray(division.questions) ? division.questions : [];
      const count = qs.filter(
        (q) =>
          q &&
          (!q.displayCondition || q.displayCondition.length === 0) &&
          q.questionId &&
          q.questionId.length > 0,
      ).length;
      return acc + count;
    }, 0);
  }, [questions]);

  const [currentSubsectionIndex, setCurrentSubsectionIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 100;
  const navigate = useNavigate();
  const [expandAll, setExpandAll] = useState(false);
  const [showGoTop, setShowGoTop] = useState(false);
  const location = useLocation();
  const { formId: allotmentId } = useParams();
  const {
    formTitle,
    formName,
    projectId,
    userId,
    templateId,
    formId: stateFormId,
    id: allotmentIdFromState,
  } = location.state || {};

  const id = allotmentId || allotmentIdFromState;
  const resolvedFormId = propFormId || stateFormId;
  const isEmployee = location.pathname.startsWith("/employee");
  const isOrgStandardViewRoute = Boolean(
    useMatch("/organization/projects/standard-forms/view/:formId"),
  );
  const isEmployeeManageViewRoute = Boolean(
    useMatch("/employee/manage-form/view/:formId"),
  );
  const hideBackButton =
    hideBackButtonProp || isOrgStandardViewRoute || isEmployeeManageViewRoute;

  const scrollableContainerRef = useRef(null);

  const scrollableContainer = {
    maxHeight: "1000px",
    overflowY: "auto",
    padding: "10px",
    borderRadius: "8px",
  };

  useEffect(() => {
    fetchSections();

    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleContainerScroll = () => {
      if (scrollableContainerRef.current) {
        const scrollTop = scrollableContainerRef.current.scrollTop;
        setShowGoTop((prev) => prev || scrollTop > 200);
      }
    };

    const containerElement = scrollableContainerRef.current;
    if (containerElement) {
      containerElement.addEventListener("scroll", handleContainerScroll);
      return () =>
        containerElement.removeEventListener("scroll", handleContainerScroll);
    }
  }, [questions, selectedSubsection]);

  const fetchSections = async () => {
    setLoading(true);
    try {
      console.log("id--->", id);
      const response = await getApi(
        {
          apiUrl: isEmployee
            ? API_URLS.GET_HIERARCHY_SECTION(id)
            : API_URLS.GET_GET_STANDARD_FORM_Section(id),
        },
        true,
      );

      if (response.success) {
        if (response.data.sections.length > 0) {
          const firstSection = response.data.sections[0];
          setSections(response.data.sections);
          setSelectedSection(firstSection.sectionId);
          await fetchSubsections(firstSection.sectionId);
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: isEmployee
          ? API_URLS.GET_HIERARCHY_SUB_SECTION(resolvedFormId, sectionId)
          : API_URLS.GET_GET_STANDARD_FORM_Subsection(id, sectionId),
      });

      if (response.success) {
        setSubsections(response.data.subsections);
        setSelectedSection(sectionId);

        if (response.data.subsections.length > 0) {
          const firstSubsection = response.data.subsections[0];
          setSelectedSubsection(firstSubsection.subsectionId);
          // Pass the IDs directly instead of relying on state
          await fetchQuestions(sectionId, firstSubsection.subsectionId);
        } else {
          setQuestions([]);
        }
      } else {
        console.error("Failed to fetch subsections");
        setSubsections([]);
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
      setSubsections([]);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (sectionId, subsectionId) => {
    // Use parameters instead of state
    const section = sectionId || selectedSection;
    const subsection = subsectionId || selectedSubsection;

    console.log("Fetching questions for:", { section, subsection });

    if (!section || !subsection) {
      console.log("Missing section or subsection ID");
      return;
    }

    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: isEmployee
          ? API_URLS.GET_EMPLOYEE_STANDARD_QUESTIONS_FROM_SUBSECTION(
              section,
              resolvedFormId,
              subsection,
            )
          : API_URLS.GET_GET_STANDARD_FORM_Questions(id, section, subsection),
      });

      console.log("Questions response:", response);

      if (response.success) {
        let divisions = response.data.divisions || [];
        setQuestions(divisions);
        console.log("Divisions set:", divisions);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching questions", error);
      message.error("Failed to fetch questions");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowGoTop(false);
  };

  const handleSubsectionClick = (subsectionId) => {
    const index = subsections.findIndex((s) => s.subsectionId === subsectionId);
    if (index !== -1) {
      setCurrentSubsectionIndex(index);
    }
    setSelectedSubsection(subsectionId);
    fetchQuestions(selectedSection, subsectionId);
  };

  const handlePrevious = () => {
    if (currentSubsectionIndex > 0) {
      const newIndex = currentSubsectionIndex - 1;
      const prevSub = subsections[newIndex];
      setCurrentSubsectionIndex(newIndex);
      setSelectedSubsection(prevSub.subsectionId);
      fetchQuestions(selectedSection, prevSub.subsectionId);
      scrollToTop();
    }
  };

  const handleNext = async () => {
    const isLast = currentSubsectionIndex >= subsections.length - 1;

    if (!isLast) {
      const newIndex = currentSubsectionIndex + 1;
      const nextSub = subsections[newIndex];
      setCurrentSubsectionIndex(newIndex);
      setSelectedSubsection(nextSub.subsectionId);
      fetchQuestions(selectedSection, nextSub.subsectionId);
      scrollToTop();
    } else {
      message.success("Subsection navigation complete.");
    }
  };

  return (
    <div className="h-full px-[20px] mb-6">
      {/* <div className="flex justify-end text-xs font-light h-4 ">
        <p>Home</p>
        <span>/</span>
        <TextStyles className="text-[#06513D]">Question Bank</TextStyles>
        <span>/</span>
        <TextStyles className="text-[#06513D]">All Questions</TextStyles>
      </div> */}
      <div className="flex justify-between h-12 pb-2">
        <div className="flex items-center">
          {!hideBackButton && (
            <Back className="cursor-pointer " onClick={() => navigate(-1)} />
          )}
          <h3 className="text-[#06513D] mt-1 text-xl">{formName}</h3>
        </div>
      </div>

      <div className="w-full bg-[#fff] rounded-t-[10px] shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="grid grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
          {sections.length > 0 ? (
            sections.map((section, index) => (
              <div
                key={index}
                className={`
                  relative
                  h-10
                  px-2
                  flex
                  items-center
                  justify-center
                  text-[13px]
                  font-medium
                  cursor-pointer
                  rounded-xl
                  mx-1
                  my-1
                  transition-all
                  duration-200
                  ease-in-out
                  border border-[#3BA2B959]
                  ${
                    selectedSection == section.sectionId
                      ? "bg-[#06513D] text-white shadow-md transform scale-105"
                      : "bg-transparent text-gray-700 hover:text-gray-900"
                  }
                  ${selectedSection === section.sectionId ? "" : "hover:shadow-sm"}
                `}
                onClick={() => fetchSubsections(section.sectionId)}
              >
                <span className="text-center leading-tight">
                  {section.name}
                </span>
              </div>
            ))
          ) : (
            <div className="col-span-5 flex items-center justify-center h-20">
              <p className="text-gray-500 text-center">No sections available</p>
            </div>
          )}
        </div>

        {selectedSection && (
          <div>
            <div className="flex flex-wrap gap-5 p-2 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
              {subsections.length > 0 ? (
                subsections.map((sub) => {
                  const isSelected = selectedSubsection === sub.subsectionId;

                  return (
                    <div
                      key={sub.subsectionId}
                      className={`p-3 rounded-xl cursor-pointer border transition-all duration-200 
                        ${
                          isSelected
                            ? "shadow-md bg-gray-100"
                            : "bg-transparent text-black"
                        }`}
                      onClick={() => handleSubsectionClick(sub.subsectionId)}
                    >
                      {sub.name}
                    </div>
                  );
                })
              ) : (
                <div className="w-full flex justify-center">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    imageStyle={{ height: 35 }}
                    description={
                      <span className="text-xs text-gray-500">
                        No subsections available
                      </span>
                    }
                    style={{ margin: "8px 0" }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#fff] w-full border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6 relative">
        <div className="mt-4 ml-3">
          <div className="flex flex-col md:flex-row items-center justify-between w-[90%] m-auto gap-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-sm font-light w-72">
                TOTAL QUESTIONS: {visibleQuestionCount}
              </p>
              <SearchBar
                width="100%"
                onSearch={(value) => setSearchTerm(value)}
                className="sm:w-[250px] md:w-[300px]"
              />
            </div>

            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-6 py-5"
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>

          <div style={scrollableContainer} ref={scrollableContainerRef}>
            {loading ? (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
              </div>
            ) : selectedSubsection ? (
              <div className="mx-20 shadow-[0_0_16px_0_#3BA2B91F]">
                {questions.length > 0 ? (
                  <QuestionUtils
                    isEmployee={isEmployee}
                    questions={questions}
                    expandAll={expandAll}
                    answers={answers}
                    setAnswers={setAnswers}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    canGoPrevious={currentSubsectionIndex > 0}
                    canGoNext={currentSubsectionIndex < subsections.length - 1}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    imageStyle={{ height: 40 }}
                    description={
                      <span className="text-xs text-gray-500">No data </span>
                    }
                    style={{ padding: "16px 0" }}
                  />
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center p-8">
                Please select a subsection to view questions.
              </p>
            )}

            {showGoTop && questions.length > 0 && (
              <Button
                type="primary"
                shape="circle"
                icon={<ArrowUpOutlined />}
                size="large"
                onClick={scrollToTop}
                style={{
                  position: "absolute",
                  bottom: "30px",
                  right: "60px",
                  background: "#06513D",
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandardFormAction;
